import { useState, useEffect, useMemo, useRef } from 'react';
import {
  Calendar,
  Filter,
  TrendingUp,
  Users,
  UserCog,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { Listbox } from '@headlessui/react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  ChartOptions,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { realAPI } from '../api/realEndpoints';
import { processDateRangeData, preserveOriginalDataStructure } from '../utils/dateRangeProcessor';
import Card from './Card';
import Shimmer from './Shimmer';

// @ts-ignore
const ChartDataLabels = require('chartjs-plugin-datalabels');

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

// Register datalabels plugin separately
ChartJS.register(ChartDataLabels);

interface GraphData {
  day?: number;
  month?: number;
  year?: number;
  date?: string;
  total: number;
  pending: number;
  accepted: number;
  completed: number;
  cancelled: number;
  rejected?: number;
  pendingApproval?: number;
}

interface BookingsGraphResponse {
  graphData: GraphData[];
  statusCounts?: {
    total: number;
    pending: number;
    accepted: number;
    cancelled: number;
    completed: number;
    pendingApproval: number;
    rejected: number;
  };
  filter: {
    type: string;
    fromDate: string;
    toDate: string;
  };
}

interface BookingsGraphProps {
  data?: BookingsGraphResponse | null;
  isLoading?: boolean;
}

export default function BookingsGraph({
  data: initialData,
  isLoading: externalLoading,
}: BookingsGraphProps) {
  const chartRef = useRef<ChartJS<'line'>>(null);
  const [graphData, setGraphData] = useState<BookingsGraphResponse | null>(initialData || null);
  const [loading, setLoading] = useState<boolean>(externalLoading ?? true);
  const [filter, setFilter] = useState<'daily' | 'weekly' | 'monthly' | 'lastMonth'>('daily');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [showPending, setShowPending] = useState(true);
  const [showAccepted, setShowAccepted] = useState(true);
  const [showCompleted, setShowCompleted] = useState(true);
  const [showCancelled, setShowCancelled] = useState(true);
  const [showRejected, setShowRejected] = useState(true);
  const [showPendingApproval, setShowPendingApproval] = useState(true);

  // Calendar states
  const [showStartCalendar, setShowStartCalendar] = useState(false);
  const [showEndCalendar, setShowEndCalendar] = useState(false);

  // Track if we've processed initial data to allow subsequent filter changes
  const initialDataProcessedRef = useRef(false);

  // Calculate current counts for display
  const getCurrentCounts = () => {
    // If statusCounts is available in the response, use it
    if (graphData?.statusCounts) {
      const cancelledCount =
        graphData.statusCounts.cancelled ??
        (graphData.statusCounts as { canceled?: number }).canceled ??
        0;
      return {
        total: graphData.statusCounts.total || 0,
        pending: graphData.statusCounts.pending || 0,
        accepted: graphData.statusCounts.accepted || 0,
        completed: graphData.statusCounts.completed || 0,
        cancelled: cancelledCount,
        rejected: graphData.statusCounts.rejected || 0,
        pendingApproval: graphData.statusCounts.pendingApproval || 0,
      };
    }

    // Fallback to latest graph data if statusCounts not available
    if (!graphData?.graphData.length)
      return {
        total: 0,
        pending: 0,
        accepted: 0,
        completed: 0,
        cancelled: 0,
        rejected: 0,
        pendingApproval: 0,
      };

    const latestData = graphData.graphData[graphData.graphData.length - 1];
    const cancelledValue =
      latestData.cancelled ?? (latestData as { canceled?: number }).canceled ?? 0;
    return {
      total: latestData.total || 0,
      pending: latestData.pending || 0,
      accepted: latestData.accepted || 0,
      completed: latestData.completed || 0,
      cancelled: cancelledValue,
      rejected: latestData.rejected || 0,
      pendingApproval: latestData.pendingApproval || 0,
    };
  };

  // Filter options
  const filterOptions = [
    { label: 'Daily', value: 'daily' },
    { label: 'Weekly', value: 'weekly' },
    { label: 'Monthly', value: 'monthly' },
    { label: 'Last Month', value: 'lastMonth' },
  ];

  // Custom Date Picker Component (from bookings page)
  const DatePicker = ({
    selectedDate,
    onDateChange,
    onClose,
    isOpen,
    minDate,
  }: {
    selectedDate?: string;
    onDateChange: (date: string) => void;
    onClose: () => void;
    isOpen: boolean;
    minDate?: string;
  }) => {
    // Smart initial month: if there's a selectedDate, use it.
    // Otherwise current date.
    // Fix: Parse YYYY-MM-DD explicitly as local components to avoid UTC timezone shifts
    const parseDateLocal = (dateStr: string) => {
      if (!dateStr) return new Date();
      const [y, m, d] = dateStr.split('-').map(Number);
      return new Date(y, m - 1, d);
    };

    const getInitialMonth = () => {
      if (selectedDate) {
        return parseDateLocal(selectedDate);
      }
      if (minDate) {
        return parseDateLocal(minDate);
      }
      return new Date();
    };

    const [currentMonth, setCurrentMonth] = useState(getInitialMonth());

    // Update current month when opening if needed
    useEffect(() => {
      if (isOpen) {
        setCurrentMonth(getInitialMonth());
      }
    }, [isOpen, selectedDate, minDate]);

    // Check if previous button should be disabled
    const isPreviousDisabled = useMemo(() => {
      if (!minDate) return false;
      const [minY, minM] = minDate.split('-').map(Number);
      return currentMonth.getFullYear() === minY && currentMonth.getMonth() === minM - 1;
    }, [currentMonth, minDate]);

    if (!isOpen) return null;

    const getDaysInMonth = (date: Date) => {
      return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
    };

    const getFirstDayOfMonth = (date: Date) => {
      return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
    };

    const formatDateForInput = (date: Date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    const handleDateSelect = (day: number) => {
      const selected = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
      onDateChange(formatDateForInput(selected));
      onClose();
    };

    const handleMonthChange = (increment: number) => {
      setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + increment, 1));
    };

    const daysInMonth = getDaysInMonth(currentMonth);
    const firstDay = getFirstDayOfMonth(currentMonth);
    const monthNames = [
      'January',
      'February',
      'March',
      'April',
      'May',
      'June',
      'July',
      'August',
      'September',
      'October',
      'November',
      'December',
    ];
    const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

    return (
      <>
        {/* Backdrop */}
        <div className="fixed inset-0 bg-black bg-opacity-50 z-40" onClick={onClose} />
        {/* Calendar */}
        <div
          className="fixed z-50 bg-white rounded-lg shadow-lg border border-gray-200 p-4 w-80"
          style={{
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            maxHeight: '90vh',
            overflowY: 'auto',
          }}
        >
          <div className="flex items-center justify-between mb-4">
            <button
              onClick={() => !isPreviousDisabled && handleMonthChange(-1)}
              disabled={isPreviousDisabled}
              className={`p-2 rounded-lg transition-colors ${
                isPreviousDisabled
                  ? 'text-gray-300 cursor-not-allowed'
                  : 'hover:bg-gray-100 text-gray-600'
              }`}
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button
              onClick={() => handleMonthChange(1)}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ChevronRight className="w-4 h-4 text-gray-600" />
            </button>
          </div>

          <div className="text-center mb-4">
            <h3 className="font-semibold text-gray-900">
              {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
            </h3>
          </div>

          <div className="grid grid-cols-7 gap-1 mb-2">
            {weekDays.map((day) => (
              <div
                key={day}
                className="h-8 flex items-center justify-center text-xs font-medium text-gray-500"
              >
                {day}
              </div>
            ))}
          </div>

          <div className="grid grid-cols-7 gap-1">
            {Array.from({ length: firstDay }).map((_, index) => (
              <div key={`empty-${index}`} className="h-10" />
            ))}
            {Array.from({ length: daysInMonth }).map((_, index) => {
              const day = index + 1;
              const currentDate = new Date(
                currentMonth.getFullYear(),
                currentMonth.getMonth(),
                day
              );
              const dateString = formatDateForInput(currentDate);
              const isSelected = selectedDate && dateString === selectedDate;
              const isToday = dateString === formatDateForInput(new Date());

              // Check if date is disabled (before minDate)
              let isDisabled = false;
              if (minDate) {
                isDisabled = dateString < minDate;
              }

              return (
                <div
                  key={day}
                  onClick={() => !isDisabled && handleDateSelect(day)}
                  className={`h-10 flex items-center justify-center text-sm cursor-pointer rounded-lg transition-colors ${
                    isDisabled
                      ? 'text-gray-300 cursor-not-allowed'
                      : isSelected
                        ? 'bg-blue-500 text-white hover:bg-blue-600 shadow-lg transform scale-105'
                        : isToday
                          ? 'bg-blue-100 text-blue-600 hover:bg-blue-200 border border-blue-300'
                          : 'hover:bg-gray-100'
                  }`}
                >
                  {day}
                </div>
              );
            })}
          </div>

          <div className="mt-4 pt-4 border-t border-gray-200 flex justify-end">
            <button
              onClick={() => {
                const today = formatDateForInput(new Date());
                if (!minDate || today >= minDate) {
                  onDateChange(today);
                  onClose();
                }
              }}
              className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Today
            </button>
          </div>
        </div>
      </>
    );
  };

  const handleResetFilters = () => {
    setFromDate('');
    setToDate('');
    setFilter('daily');
    setShowStartCalendar(false);
    setShowEndCalendar(false);
    // Reset all checkbox states to visible
    setShowPending(true);
    setShowAccepted(true);
    setShowCompleted(true);
    setShowCancelled(true);
    setShowRejected(true);
    setShowPendingApproval(true);
    // Call fetchGraphData after resetting filters to ensure data refresh
    setTimeout(() => fetchGraphData(), 0);
  };

  const validateAndSetToDate = (date: string) => {
    if (date && !fromDate) {
      // If toDate is selected but fromDate is not, show error but still set date
      setError('Please select start date first');
      setToDate(date); // Set the date anyway to prevent UI mismatch
      // Auto-hide error after 3 seconds
      setTimeout(() => setError(null), 3000);
      return;
    }

    // Validate date range
    if (fromDate && date) {
      const start = new Date(fromDate);
      const end = new Date(date);
      if (end < start) {
        setError('End date must be after start date');
        setToDate(date); // Set the date anyway to prevent UI mismatch
        setTimeout(() => setError(null), 3000);
        return;
      }
    }

    setToDate(date);
    setError(null);
  };

  const fetchGraphData = async () => {
    setLoading(true);
    setError(null);
    try {
      // Validate date range before making API call
      if (fromDate && toDate) {
        const start = new Date(fromDate);
        const end = new Date(toDate);

        if (end < start) {
          setError('End date must be after start date');
          setLoading(false);
          return;
        }
      }

      // If dates are selected, only send dates, no filter
      const data = await realAPI.dashboard.getBookingsGraph(
        fromDate || toDate ? undefined : filter,
        fromDate || undefined,
        toDate || undefined
      );

      if (data && data.graphData) {
        // Process data to handle multi-month date ranges correctly
        if (fromDate && toDate) {
          const processedGraphData = processDateRangeData(
            data.graphData,
            fromDate,
            toDate,
            'daily'
          );

          // Convert back to expected format while preserving original structure
          const processedData = {
            ...data,
            graphData: preserveOriginalDataStructure(data.graphData, processedGraphData),
          };
          setGraphData(processedData);
        } else {
          setGraphData(data);
        }
      } else {
        console.warn('No bookings graph data received:', data);
        setGraphData(null);
      }
    } catch (error) {
      console.error('Failed to fetch bookings graph data:', error);
      setError('Failed to fetch data');
      setGraphData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Handle initial data from Dashboard (one-time setup)
    if (initialData && !initialDataProcessedRef.current) {
      setGraphData(initialData);
      // Sync filter with the data received from dashboard
      if (initialData.filter?.type) {
        setFilter(initialData.filter.type as any);
      }
      setLoading(false);
      initialDataProcessedRef.current = true;
    } else if (!initialData && !initialDataProcessedRef.current) {
      // No initial data provided, fetch it
      fetchGraphData();
      initialDataProcessedRef.current = true;
    }
  }, [initialData]); // Only re-run if initialData changes

  useEffect(() => {
    // Handle filter changes (after initial setup)
    if (initialDataProcessedRef.current && filter) {
      fetchGraphData();
    }
  }, [filter]); // Only re-run when filter changes

  useEffect(() => {
    // Fetch when both dates are selected, or when both are cleared
    if ((fromDate && toDate) || (!fromDate && !toDate)) {
      fetchGraphData();
    }
  }, [fromDate, toDate]);

  const getMaxValue = () => {
    if (!graphData?.graphData.length) return 10;
    return Math.max(...graphData.graphData.map((d) => d.total), 10);
  };

  const getBarHeight = (value: number) => {
    const maxValue = getMaxValue();
    return (value / maxValue) * 100;
  };

  const getLabel = (item: GraphData) => {
    // For weekly filter, prioritize showing day names based on the date field
    if (filter === 'weekly') {
      const dayNames = [
        'Sunday',
        'Monday',
        'Tuesday',
        'Wednesday',
        'Thursday',
        'Friday',
        'Saturday',
      ];
      // If we have a date, use it to get the correct day of week
      if (item.date) {
        const date = new Date(item.date);
        return dayNames[date.getDay()];
      }
      // Otherwise try to use the day number (0-6)
      if (item.day !== undefined) {
        return dayNames[item.day % 7];
      }
      // Fallback
      return dayNames[(item.day || 1) % 7];
    }

    // If both dates are selected, show actual dates
    if (fromDate && toDate) {
      // Format the date from the item (assuming item has date property or we can construct it)
      if (item.date) {
        return new Date(item.date).toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
        });
      } else if (item.year && item.month && item.day) {
        return new Date(item.year, item.month - 1, item.day).toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
        });
      } else {
        // When dates are selected but no date info in item, construct from day and calculate date
        if (item.day) {
          const startDate = new Date(fromDate);
          const resultDate = new Date(startDate);
          resultDate.setDate(startDate.getDate() + (item.day - 1));
          return resultDate.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
          });
        }
        // Fallback to day number if no date info available
        return `Day ${item.day || ''}`;
      }
    }

    // Original logic for when no dates are selected - with better names
    if (filter === 'monthly') {
      const monthNames = [
        'January',
        'February',
        'March',
        'April',
        'May',
        'June',
        'July',
        'August',
        'September',
        'October',
        'November',
        'December',
      ];
      return monthNames[(item.month || 1) - 1];
    } else if (filter === 'lastMonth') {
      // For last month filter, show Day 1 to last day of month
      return `Day ${item.day || ''}`;
    } else {
      // For daily filter, show more descriptive day names
      return `Day ${item.day || ''}`;
    }
  };

  const getGradient = () => {
    return 'from-blue-500 to-cyan-400';
  };

  const getBgColor = () => {
    return 'bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20';
  };

  const getTextColor = () => {
    return 'text-blue-600 dark:text-blue-400';
  };

  // Function to process and fill missing data points based on filter type
  const processGraphData = (data: any[]) => {
    if (!data.length) return data;

    if (filter === 'lastMonth') {
      const lastMonth = new Date();
      lastMonth.setMonth(lastMonth.getMonth() - 1);
      const daysInMonth = new Date(lastMonth.getFullYear(), lastMonth.getMonth() + 1, 0).getDate();
      const filledData = [];
      for (let day = 1; day <= daysInMonth; day++) {
        const existingData = data.find((item) => item.day === day);
        if (existingData) {
          filledData.push(existingData);
        } else {
          // Fill missing day with zero values
          filledData.push({
            day,
            total: 0,
            pending: 0,
            accepted: 0,
            completed: 0,
            cancelled: 0,
            rejected: 0,
            pendingApproval: 0,
          });
        }
      }
      return filledData;
    }

    // For weekly and other filter types, return the data as-is from the API
    // The API already provides properly formatted data with date fields
    return data;
  };

  // Prepare chart data
  const processedGraphData = processGraphData(graphData?.graphData || []);
  const chartData = {
    labels: processedGraphData.map((item) => getLabel(item)),
    datasets: [
      // {
      //   label: 'Total',
      //   statusKey: 'total',
      //   data: processedGraphData.map((item) => item.total),
      //   borderColor: 'rgba(59, 130, 246, 0.9)',
      //   backgroundColor: 'rgba(59, 130, 246, 0.2)',
      //   borderWidth: 2,
      //   pointRadius: 4,
      //   pointHoverRadius: 6,
      //   pointBackgroundColor: 'rgb(59, 130, 246)',
      //   pointBorderColor: '#fff',
      //   pointBorderWidth: 2,
      //   tension: 0.4,
      //   fill: true,
      //   hidden: !showTotal,
      // },
      {
        label: 'Pending',
        statusKey: 'pending',
        data: processedGraphData.map((item) => item.pending),
        borderColor: 'rgba(251, 191, 36, 0.9)',
        backgroundColor: 'rgba(251, 191, 36, 0.25)',
        borderWidth: 2,
        pointRadius: 4,
        pointHoverRadius: 6,
        pointBackgroundColor: 'rgb(251, 191, 36)',
        pointBorderColor: '#fff',
        pointBorderWidth: 2,
        tension: 0.4,
        fill: true,
        hidden: !showPending,
      },

      {
        label: 'Cancelled',
        statusKey: 'cancelled',
        data: processedGraphData.map(
          (item) => item.cancelled ?? (item as { canceled?: number }).canceled ?? 0
        ),
        borderColor: 'rgba(239, 68, 68, 0.9)',
        backgroundColor: 'rgba(239, 68, 68, 0.25)',
        borderWidth: 2,
        pointRadius: 4,
        pointHoverRadius: 6,
        pointBackgroundColor: 'rgb(239, 68, 68)',
        pointBorderColor: '#fff',
        pointBorderWidth: 2,
        tension: 0.4,
        fill: true,
        hidden: !showCancelled,
      },

      {
        label: 'Rejected',
        statusKey: 'rejected',
        data: processedGraphData.map((item) => item.rejected ?? 0),
        borderColor: 'rgba(220, 38, 38, 0.9)',
        backgroundColor: 'rgba(220, 38, 38, 0.25)',
        borderWidth: 2,
        pointRadius: 4,
        pointHoverRadius: 6,
        pointBackgroundColor: 'rgb(220, 38, 38)',
        pointBorderColor: '#fff',
        pointBorderWidth: 2,
        tension: 0.4,
        fill: true,
        hidden: !showRejected,
      },

      {
        label: 'Accepted',
        statusKey: 'accepted',
        data: processedGraphData.map((item) => item.accepted),
        borderColor: 'rgba(99, 102, 241, 0.9)',
        backgroundColor: 'rgba(99, 102, 241, 0.25)',
        borderWidth: 2,
        pointRadius: 4,
        pointHoverRadius: 6,
        pointBackgroundColor: 'rgb(99, 102, 241)',
        pointBorderColor: '#fff',
        pointBorderWidth: 2,
        tension: 0.4,
        fill: true,
        hidden: !showAccepted,
      },

      {
        label: 'Pending Approval',
        statusKey: 'pendingApproval',
        data: processedGraphData.map((item) => item.pendingApproval ?? 0),
        borderColor: 'rgba(6, 182, 212, 0.9)',
        backgroundColor: 'rgba(6, 182, 212, 0.25)',
        borderWidth: 2,
        pointRadius: 4,
        pointHoverRadius: 6,
        pointBackgroundColor: 'rgb(6, 182, 212)',
        pointBorderColor: '#fff',
        pointBorderWidth: 2,
        tension: 0.4,
        fill: true,
        hidden: !showPendingApproval,
      },

      {
        label: 'Completed',
        statusKey: 'completed',
        data: processedGraphData.map((item) => item.completed),
        borderColor: 'rgba(34, 197, 94, 0.9)',
        backgroundColor: 'rgba(34, 197, 94, 0.25)',
        borderWidth: 2,
        pointRadius: 4,
        pointHoverRadius: 6,
        pointBackgroundColor: 'rgb(34, 197, 94)',
        pointBorderColor: '#fff',
        pointBorderWidth: 2,
        tension: 0.4,
        fill: true,
        hidden: !showCompleted,
      },
    ],
  };

  // If no data, create default data with sample values
  if (!graphData?.graphData.length) {
    const defaultLabels =
      filter === 'monthly'
        ? ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun']
        : filter === 'weekly'
          ? ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
          : ['Day 1', 'Day 2', 'Day 3', 'Day 4', 'Day 5', 'Day 6'];

    chartData.labels = defaultLabels;
    // Show sample default values instead of zeros for all datasets
    const sampleData = [12, 19, 8, 15, 22, 18].slice(0, defaultLabels.length);
    chartData.datasets.forEach((dataset) => {
      dataset.data = sampleData;
    });
  }

  // Adjust point radius based on data length for better appearance
  const dataLength = processedGraphData.length || chartData.labels.length;
  if (dataLength <= 7) {
    // For short date ranges, make points more visible
    chartData.datasets.forEach((dataset) => {
      dataset.pointRadius = 6;
      dataset.pointHoverRadius = 8;
      dataset.borderWidth = 4;
    });
  } else if (dataLength <= 14) {
    // For medium ranges
    chartData.datasets.forEach((dataset) => {
      dataset.pointRadius = 4;
      dataset.pointHoverRadius = 6;
      dataset.borderWidth = 3;
    });
  } else {
    // For long ranges, make points smaller but keep line visible
    chartData.datasets.forEach((dataset) => {
      dataset.pointRadius = 2;
      dataset.pointHoverRadius = 4;
      dataset.borderWidth = 2.5;
    });
  }

  // Ensure the line is continuous and smooth for all filter types
  chartData.datasets.forEach((dataset) => {
    dataset.tension = 0.4;
  }); // Makes the line smooth

  const chartOptions: ChartOptions<'line'> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'top',
        align: 'center',
        labels: {
          boxWidth: 14,
          padding: 8,
          usePointStyle: true,
          pointStyle: 'circle', // Changed from 'rect' to 'circle'
          font: {
            size: 12,
            weight: 'bold',
          },
          generateLabels: function (chart: any) {
            const datasets = chart.data.datasets;
            const counts = getCurrentCounts();
            const showStates = [
              showPending,
              showCancelled,
              showRejected,
              showAccepted,
              showPendingApproval,
              showCompleted,
            ];
            return datasets.map((dataset: any, i: number) => {
              let key = dataset.statusKey as keyof typeof counts | undefined;
              if (!key) {
                const label = dataset.label?.toString().toLowerCase() || '';
                const labelMap: Record<string, keyof typeof counts> = {
                  pending: 'pending',
                  accepted: 'accepted',
                  completed: 'completed',
                  cancelled: 'cancelled',
                  rejected: 'rejected',
                  'pending approval': 'pendingApproval',
                };
                key = labelMap[label] || 'total';
              }
              const count = counts[key] || 0;
              const isHidden = !showStates[i];

              return {
                text: `${dataset.label} (${count})`,
                fillStyle: isHidden ? 'transparent' : dataset.borderColor,
                hidden: isHidden,
                index: i,
                fontColor: isHidden ? '#9ca3af' : '#374151',
                font: {
                  weight: isHidden ? 'normal' : 'bold',
                },
                // Use dataset color for border even when hidden
                strokeStyle: dataset.borderColor,
                lineWidth: 2,
                // Custom draw function to show cross mark for hidden items
                drawCross: isHidden,
              };
            });
          },
        },
        afterDraw: (chart: any) => {
          // Draw checkmarks and crosses for legend items
          const legend = chart.legend;
          if (legend && legend.legendItems) {
            legend.legendItems.forEach((legendItem: any) => {
              const ctx = chart.ctx;
              const { x, y, width, height } = legendItem;

              if (legendItem.drawCross) {
                // Draw cross mark (X) for hidden items
                ctx.save();
                ctx.strokeStyle = legendItem.strokeStyle; // Use dataset color for cross
                ctx.lineWidth = 2;
                ctx.beginPath();

                const padding = 2;
                ctx.moveTo(x + padding, y + padding);
                ctx.lineTo(x + width - padding, y + height - padding);
                ctx.moveTo(x + width - padding, y + padding);
                ctx.lineTo(x + padding, y + height - padding);

                ctx.stroke();
                ctx.restore();
              } else {
                // Draw checkmark (✔) for visible items
                ctx.save();
                ctx.strokeStyle = '#ffffff'; // White checkmark
                ctx.lineWidth = 3; // Thicker line for better visibility
                ctx.lineCap = 'round'; // Rounded ends for smoother look
                ctx.lineJoin = 'round'; // Rounded corners
                ctx.beginPath();

                // Draw checkmark centered in the circle
                const centerX = x + width / 2;
                const centerY = y + height / 2;
                const checkSize = Math.min(width, height) * 0.4; // Larger size

                // Start from left-middle, go to center, then to top-right
                ctx.moveTo(centerX - checkSize * 0.4, centerY);
                ctx.lineTo(centerX - checkSize * 0.1, centerY + checkSize * 0.3);
                ctx.lineTo(centerX + checkSize * 0.5, centerY - checkSize * 0.35);

                ctx.stroke();
                ctx.restore();
              }
            });
          }
        },
        onClick: (e: any, legendItem: any, legend: any) => {
          const index = legendItem.index;
          const showStates = [
            showPending,
            showCancelled,
            showRejected,
            showAccepted,
            showPendingApproval,
            showCompleted,
          ];

          // Prevent deselecting the last visible dataset
          const visibleCount = showStates.filter((state) => state).length;
          if (visibleCount === 1 && showStates[index]) {
            return; // Can't deselect the last visible dataset
          }

          // Toggle the checkbox state
          switch (index) {
            case 0:
              setShowPending(!showPending);
              break;
            case 1:
              setShowCancelled(!showCancelled);
              break;
            case 2:
              setShowRejected(!showRejected);
              break;
            case 3:
              setShowAccepted(!showAccepted);
              break;
            case 4:
              setShowPendingApproval(!showPendingApproval);
              break;
            case 5:
              setShowCompleted(!showCompleted);
              break;
            default:
              break;
          }
        },
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: '#fff',
        bodyColor: '#fff',
        padding: 12,
        displayColors: true,
        callbacks: {
          title: (context: any) => context[0].label,
          label: (context: any) => {
            const datasetLabel = context.dataset.label;
            const value = context.parsed.y;
            return `${datasetLabel}: ${value}`;
          },
        },
      },
      datalabels: {
        display: true,
        formatter: (value: any, context: any) => {
          // Don't show labels for Total Bookings
          if (context.dataset?.label === 'Total') {
            return '';
          }
          // Show status name for other datasets
          const datasetLabel = context.dataset.label;
          return datasetLabel;
        },
        color: 'black',
        backgroundColor: (context: any) => {
          // Use the dataset's color for background
          const colors = [
            'rgba(251, 191, 36, 0.9)', // Pending
            'rgba(239, 68, 68, 0.9)', // Cancelled
            'rgba(220, 38, 38, 0.9)', // Rejected
            'rgba(99, 102, 241, 0.9)', // Accepted
            'rgba(6, 182, 212, 0.9)', // Pending approval
            'rgba(34, 197, 94, 0.9)', // Completed
          ];
          return colors[context.datasetIndex] || 'rgba(59, 130, 246, 0.9)';
        },
        borderRadius: 10,
        borderWidth: 1,
        borderColor: 'white',
        font: {
          weight: 'bold',
          size: 10,
        },
        padding: {
          top: 2,
          bottom: 2,
          left: 4,
          right: 4,
        },
        align: 'center',
        anchor: 'center',
        offset: 0,
      },
    } as any,
    scales: {
      x: {
        display: true,
        grid: {
          display: false,
        },
        ticks: {
          color: '#6b7280',
          font: {
            size: dataLength <= 7 ? 12 : 11,
            weight: dataLength <= 7 ? 'bold' : 'normal',
          },
          // For short ranges, show all labels; for longer ranges, skip some
          autoSkip: dataLength > 10,
          maxRotation: dataLength <= 7 ? 0 : 45,
          minRotation: dataLength <= 7 ? 0 : 45,
        },
      },
      y: {
        display: false,
        beginAtZero: true,
        grid: {
          display: false,
        },
      },
    },
    interaction: {
      intersect: true,
      mode: 'point' as const,
    },
  };

  return (
    <Card className={`${getBgColor()} border-0 relative overflow-hidden`}>
      <div className="p-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className={`text-lg font-semibold ${getTextColor()}`}>Booking Analytics</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {filter.charAt(0).toUpperCase() + filter.slice(1)} Overview
            </p>
          </div>
          <div className={`p-2 rounded-lg bg-gradient-to-br ${getGradient()} text-white`}>
            <TrendingUp className="w-5 h-5" />
          </div>
        </div>

        {/* Error display */}
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center justify-between animate-pulse">
            <div className="flex items-center">
              <div className="w-4 h-4 bg-red-500 rounded-full mr-3"></div>
              <span className="text-red-700 text-sm font-medium">{error}</span>
            </div>
            <button
              onClick={() => setError(null)}
              className="text-red-400 hover:text-red-600 transition-colors"
            >
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                  clipRule="evenodd"
                />
              </svg>
            </button>
          </div>
        )}

        <div className="flex flex-col gap-4">
          <div className="flex flex-wrap items-end gap-3 sm:gap-4">
            {/* Filter Dropdown */}
            {!(fromDate || toDate) && (
              <div className="w-full sm:w-40 flex-shrink-0">
                <label className="block text-xs font-medium text-gray-500 mb-1.5 ml-1">
                  Time Range
                </label>
                <div className="relative">
                  <Listbox value={filter} onChange={setFilter}>
                    <Listbox.Button className="w-full h-10 pl-3 pr-10 text-left bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all text-sm">
                      <span className="block truncate">
                        {filterOptions.find((o) => o.value === filter)?.label}
                      </span>
                      <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                    </Listbox.Button>
                    <Listbox.Options className="absolute z-20 mt-1.5 w-full bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 py-1 overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200">
                      {filterOptions.map((option) => (
                        <Listbox.Option
                          key={option.value}
                          value={option.value}
                          className={({ active }) =>
                            `px-4 py-2.5 cursor-pointer transition-colors text-sm ${active ? 'bg-primary-50 text-primary-700 dark:bg-primary-900/20 dark:text-primary-400' : 'text-gray-700 dark:text-gray-300'}`
                          }
                        >
                          {option.label}
                        </Listbox.Option>
                      ))}
                    </Listbox.Options>
                  </Listbox>
                </div>
              </div>
            )}

            {/* Date Inputs Group */}
            <div className="flex flex-wrap items-end gap-3 sm:gap-4 flex-1">
              {/* Start Date */}
              <div className="w-full sm:w-auto min-w-[140px] flex-1">
                <label className="block text-xs font-medium text-gray-500 mb-1.5 ml-1">From</label>
                <div className="relative">
                  <button
                    onClick={() => setShowStartCalendar(true)}
                    className="w-full h-10 pl-10 pr-3 text-left bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all text-sm hover:border-gray-400"
                  >
                    <Calendar className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <span className={fromDate ? 'text-gray-900 dark:text-white' : 'text-gray-400'}>
                      {fromDate
                        ? new Date(fromDate).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric',
                          })
                        : 'Select date'}
                    </span>
                  </button>
                  <DatePicker
                    selectedDate={fromDate}
                    onDateChange={(date) => setFromDate(date)}
                    onClose={() => setShowStartCalendar(false)}
                    isOpen={showStartCalendar}
                  />
                </div>
              </div>

              {/* End Date */}
              <div className="w-full sm:w-auto min-w-[140px] flex-1">
                <label className="block text-xs font-medium text-gray-500 mb-1.5 ml-1">To</label>
                <div className="relative">
                  <button
                    onClick={() => setShowEndCalendar(true)}
                    className="w-full h-10 pl-10 pr-3 text-left bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all text-sm hover:border-gray-400"
                  >
                    <Calendar className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <span className={toDate ? 'text-gray-900 dark:text-white' : 'text-gray-400'}>
                      {toDate
                        ? new Date(toDate).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric',
                          })
                        : 'Select date'}
                    </span>
                  </button>
                  <DatePicker
                    selectedDate={toDate}
                    onDateChange={(date) => validateAndSetToDate(date)}
                    onClose={() => setShowEndCalendar(false)}
                    isOpen={showEndCalendar}
                    minDate={fromDate}
                  />
                </div>
              </div>

              {/* Reset Button */}
              {(fromDate || toDate || filter !== 'daily') && (
                <button
                  onClick={handleResetFilters}
                  className="h-10 px-5 text-sm font-medium bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded-xl hover:bg-gray-200 dark:hover:bg-gray-600 transition-all flex-shrink-0 mb-[1px]"
                >
                  Reset
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Graph */}
        <div className="h-[18rem] mt-6">
          {loading ? (
            <div className="h-full flex items-center justify-center">
              <div className="loader"></div>
            </div>
          ) : error ? (
            <div className="h-full flex items-center justify-center">
              <div className="text-center max-w-sm">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg
                    className="w-8 h-8 text-red-500"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">Unable to Load Data</h3>
                <p className="text-sm text-gray-600 mb-4">{error}</p>
                <button
                  onClick={fetchGraphData}
                  className="inline-flex items-center px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors text-sm font-medium"
                >
                  <svg
                    className="w-4 h-4 mr-2"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                    />
                  </svg>
                  Retry
                </button>
              </div>
            </div>
          ) : (
            <div className="h-full w-full">
              <Line ref={chartRef} data={chartData} options={chartOptions} />
            </div>
          )}
        </div>
      </div>
    </Card>
  );
}
